<!-- eslint-disable -->
<template>
  <v-container class="profile-container">
    <v-row>
      <v-col cols="12">
        <!-- Profile Header -->
        <v-card elevation="2" class="mb-4">
          <v-card-text class="pa-6">
            <div class="d-flex align-start" style="gap: 2rem;">
              <!-- Avatar with online indicator -->
              <div class="position-relative">
                <v-avatar size="120" color="primary">
                  <v-img v-if="profile?.avatarUrl" :src="profile.avatarUrl"></v-img>
                  <span v-else class="text-h2">{{ profile?.displayName?.charAt(0) || 'U' }}</span>
                </v-avatar>
                <!-- Online status indicator (only on other profiles) -->
                <span 
                  v-if="!isOwnProfile && profile?.onlineStatus && profile.onlineStatus !== 'hidden'"
                  class="online-indicator-large"
                  :class="profile.onlineStatus"
                ></span>
              </div>

              <!-- Profile Info -->
              <div class="flex-grow-1">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="d-flex align-center" style="gap: 0.75rem;">
                    <h1 class="text-h4">{{ profile?.displayName || profile?.username }}</h1>
                    <!-- Online status chip -->
                    <v-chip 
                      v-if="!isOwnProfile && profile?.onlineStatus === 'online'" 
                      size="small" 
                      color="success"
                    >
                      <v-icon start size="small">mdi-circle</v-icon>
                      {{ $t('profile.online') }}
                    </v-chip>
                    <v-chip 
                      v-else-if="!isOwnProfile && profile?.onlineStatus === 'away'" 
                      size="small" 
                      color="warning"
                    >
                      <v-icon start size="small">mdi-circle</v-icon>
                      {{ $t('profile.away') }}
                    </v-chip>
                  </div>
                  <!-- Friend Button (only show on other people's profiles) -->
                  <div v-if="profile?.userId && !isOwnProfile" class="d-flex align-center" style="gap: 8px;">
                    <friend-button :user-id="profile.userId" />
                    <v-btn 
                      color="primary" 
                      variant="outlined"
                      size="small"
                      @click="startChat"
                    >
                      <v-icon start size="small">mdi-chat</v-icon>
                      {{ $t('chat.sendMessage') }}
                    </v-btn>
                  </div>
                </div>
                <div class="text-subtitle-1 text-grey mb-3">@{{ profile?.username }}</div>

                <div v-if="profile?.bio" class="mb-3">
                  {{ profile.bio }}
                </div>

                <div class="d-flex flex-wrap" style="gap: 1.5rem;">
                  <div v-if="profile?.location">
                    <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                    {{ profile.location }}
                  </div>
                  <div v-if="profile?.website">
                    <v-icon size="small" class="mr-1">mdi-web</v-icon>
                    <a :href="profile.website" target="_blank" rel="noopener">{{ profile.website }}</a>
                  </div>
                  <div v-if="profile?.age">
                    <v-icon size="small" class="mr-1">mdi-cake-variant</v-icon>
                    {{ profile.age }} {{ $t('profile.age') }}
                  </div>
                  <div>
                    <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
                    {{ $t('profile.joined') }} {{ formatDate(profile?.joinDate) }}
                  </div>
                  <!-- Last seen (if offline) -->
                  <div v-if="!isOwnProfile && profile?.onlineStatus === 'offline' && profile?.lastSeen">
                    <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                    {{ $t('profile.lastSeen') }} {{ formatRelativeTime(profile.lastSeen) }}
                  </div>
                </div>

                <!-- Stats -->
                <div class="d-flex mt-4" style="gap: 2rem;">
                  <div>
                    <div class="text-h6">{{ profile?.postCount || 0 }}</div>
                    <div class="text-caption text-grey">{{ $t('profile.posts') }}</div>
                  </div>
                  <div>
                    <div class="text-h6">{{ profile?.commentCount || 0 }}</div>
                    <div class="text-caption text-grey">{{ $t('profile.comments') }}</div>
                  </div>
                  <div>
                    <div class="text-h6">{{ profile?.reactionCount || 0 }}</div>
                    <div class="text-caption text-grey">{{ $t('profile.reactions') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Tabs -->
        <v-tabs v-model="tab" bg-color="white" color="primary">
          <v-tab value="overview">{{ $t('profile.overview') }}</v-tab>
          <v-tab v-if="isOwnProfile" value="edit">{{ $t('profile.edit') }}</v-tab>
          <v-tab v-if="isOwnProfile" value="settings">{{ $t('profile.settings') }}</v-tab>
          <v-tab value="activity">{{ $t('profile.activity') }}</v-tab>
        </v-tabs>

        <v-window v-model="tab" class="mt-4">
          <!-- Overview Tab -->
          <v-window-item value="overview">
            <profile-overview :profile="profile" :activity="activity" />
          </v-window-item>

          <!-- Edit Profile Tab -->
          <v-window-item v-if="isOwnProfile" value="edit">
            <profile-edit :profile="profile" @updated="loadProfile" />
          </v-window-item>

          <!-- Settings Tab -->
          <v-window-item v-if="isOwnProfile" value="settings">
            <profile-settings :profile="profile" @updated="loadProfile" />
          </v-window-item>

          <!-- Activity Tab -->
          <v-window-item value="activity">
            <profile-activity :activity="activity" :loading="loadingActivity" />
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, watch, inject } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import api from '../services/api'
import ProfileOverview from '../components/ProfileOverview.vue'
import ProfileEdit from '../components/ProfileEdit.vue'
import ProfileSettings from '../components/ProfileSettings.vue'
import ProfileActivity from '../components/ProfileActivity.vue'
import FriendButton from '../components/FriendButton.vue'

const route = useRoute()
const store = useStore()

// Inject chat function from App.vue
const startChatWithUser = inject('startChatWithUser')

const tab = ref('overview')
const profile = ref(null)
const activity = ref(null)
const loadingActivity = ref(false)

const currentUser = computed(() => store.getters['auth/user'])
const isOwnProfile = computed(() => {
  return currentUser.value?.userId === profile.value?.userId
})

// Start chat with this profile
const startChat = () => {
  if (profile.value && startChatWithUser) {
    startChatWithUser(profile.value.userId, {
      userId: profile.value.userId,
      username: profile.value.username,
      displayName: profile.value.displayName,
      avatarUrl: profile.value.avatarUrl,
      onlineStatus: profile.value.onlineStatus
    })
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('da-DK', {
    month: 'long',
    year: 'numeric'
  })
}

const formatRelativeTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString()
}

const loadProfile = async () => {
  try {
    // Get user by username to find userId
    const userResponse = await api.get(`/users`, { params: { username: route.params.username } })
    const userId = userResponse.data.user.userId
    
    // Get full profile by userId
    const response = await api.get(`/profile/${userId}`)
    profile.value = response.data.profile
    
    // Load activity after we have the profile
    loadActivity()
  } catch (error) {
    console.error('Error loading profile:', error)
  }
}

const loadActivity = async () => {
  if (!profile.value?.userId) return
  
  loadingActivity.value = true
  try {
    const response = await api.get(`/profile/${profile.value.userId}/activity`)
    activity.value = response.data.activity
  } catch (error) {
    console.error('Error loading activity:', error)
  } finally {
    loadingActivity.value = false
  }
}

onMounted(() => {
  loadProfile()
})

// Watch for route changes (e.g., navigating from one profile to another)
watch(() => route.params.username, (newUsername, oldUsername) => {
  if (newUsername && newUsername !== oldUsername) {
    tab.value = 'overview' // Reset to overview tab
    profile.value = null // Clear old profile
    activity.value = null
    loadProfile()
  }
})
</script>

<style scoped>
.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 2rem;
}

a {
  color: #4A8B7F;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.position-relative {
  position: relative;
}

.online-indicator-large {
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 3px solid white;
}

.online-indicator-large.online {
  background-color: #4CAF50;
}

.online-indicator-large.away {
  background-color: #FFC107;
}

.online-indicator-large.offline {
  background-color: #9E9E9E;
}
</style>